import { memo, useEffect, useState } from "react";

import {
  CONNECTION_ERROR_MESSAGE,
  DEFAULT_CHANNEL,
  PHONE_NUMBER_PREFIX,
  SUCCESS_API_CODE,
} from "@zenith/data/constants";
import {
  OutletApiService,
  OutletListDTO,
  UtilityApiService,
} from "@zenith/services/user";
import { CreateTeamMemberRequest } from "@zenith/services/user/models/CreateTeamMemberRequest";
import { TeamManagementControllerService } from "@zenith/services/user/services/TeamManagementControllerService";
import { useSettingsContext } from "@zenith/context";
import { encrypt } from "@zenith/utils";
import { getErrorTitle } from "@zenith/utils/Helpers/getAPIErrorTitle";
import { TeamMemberRole } from "../../data/roles";
import { useBusinessType } from "../../hooks/useBusinessType";
import SelectRoleModal from "../SelectRoleModal/select-role-modal";
import RolePermissionModal from "../RolePermissionModal/role-permission-modal";
import TeamMemberDetailsModal, {
  TeamMemberDetails,
} from "../TeamMemberDetailsModal/team-member-details-modal";
import CreatePasscodeModal from "../CreatePasscodeModal/create-passcode-modal";
import TeamMemberSuccessModal from "../TeamMemberSuccessModal/team-member-success-modal";

type Step = "role" | "breakdown" | "details" | "passcode" | "success";

const API_ROLES: Record<TeamMemberRole, CreateTeamMemberRequest.role> = {
  [TeamMemberRole.ADMIN]: CreateTeamMemberRequest.role.ADMIN,
  [TeamMemberRole.MANAGER]: CreateTeamMemberRequest.role.MANAGER,
  [TeamMemberRole.ACCOUNTANT]: CreateTeamMemberRequest.role.ACCOUNTANT,
  [TeamMemberRole.CASHIER]: CreateTeamMemberRequest.role.CASHIER,
};

// The default passcode goes to the API RSA-encrypted. Each field is
// encrypted on its own, since the ciphertext differs every time.
const encryptPasscode = async (passcode: string) => {
  const response = await UtilityApiService.getPublicKey(
    DEFAULT_CHANNEL,
    import.meta.env.VITE_X_API_KEY
  );
  const publicKey = response.data?.publicKeyBase64;
  const defaultPassword = publicKey && encrypt(passcode, publicKey);
  const confirmDefaultPassword = publicKey && encrypt(passcode, publicKey);
  if (!defaultPassword || !confirmDefaultPassword) {
    throw new Error("Could not encrypt the passcode");
  }
  return { defaultPassword, confirmDefaultPassword };
};

export interface AddTeamMemberFlowProps {
  showModal: boolean;
  notify: (
    title: string,
    message: string,
    status: "success" | "error" | "warning"
  ) => void;
}

const AddTeamMemberFlow = memo(function AddTeamMemberFlow(
  props: AddTeamMemberFlowProps
) {
  const { showModal, notify } = props;
  const { settingsContext, setSettingsContext } = useSettingsContext();
  const businessType = useBusinessType();

  const [step, setStep] = useState<Step>("role");
  const [role, setRole] = useState<TeamMemberRole>(TeamMemberRole.ADMIN);
  // Select Role opens with nothing chosen, so the design's disabled Continue
  // state is reachable. `role` still holds a concrete value for the screens
  // that only open once a role has been picked (breakdown, details).
  const [hasChosenRole, setHasChosenRole] = useState(false);
  const [details, setDetails] = useState<TeamMemberDetails | null>(null);
  const [outlets, setOutlets] = useState<OutletListDTO[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [passcodeCreated, setPasscodeCreated] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{
    phoneNumber?: string;
    email?: string;
  }>({});

  useEffect(() => {
    if (!showModal) return;
    // The permitted outlets are the ones this user can map a member to; the
    // merchant-wide list endpoint can come back empty for the same session.
    OutletApiService.getPermittedOutlets(true)
      .then((response) =>
        setOutlets(
          (response.data?.outlets ?? []).map((outlet) => ({
            id: outlet.id,
            name: outlet.name,
            status: outlet.status
              ? (outlet.status as OutletListDTO.status)
              : undefined,
          }))
        )
      )
      .catch(() => setOutlets([]));
  }, [showModal]);

  const resetFlow = () => {
    setStep("role");
    setRole(TeamMemberRole.ADMIN);
    setHasChosenRole(false);
    setDetails(null);
    setPasscodeCreated(false);
    setFieldErrors({});
  };

  const closeFlow = () => {
    resetFlow();
    setSettingsContext({ ...settingsContext, showModal: false });
  };

  // Without a passcode the member is invited by email and stays PENDING
  // until they accept; with one they're ACTIVE straight away.
  const submitTeamMember = async (
    memberDetails: TeamMemberDetails,
    passcode?: string
  ) => {
    setIsSaving(true);
    try {
      const response = await TeamManagementControllerService.create({
        email: memberDetails.email,
        firstName: memberDetails.firstName,
        lastName: memberDetails.lastName,
        phoneNumber: PHONE_NUMBER_PREFIX + memberDetails.phoneNumber,
        role: API_ROLES[memberDetails.role],
        // Admins cover every outlet, which the API expects as an empty list
        outletIds:
          memberDetails.role === TeamMemberRole.ADMIN
            ? []
            : memberDetails.outletIds,
        ...(passcode ? await encryptPasscode(passcode) : {}),
      });
      if (response.data && response.internalCode === SUCCESS_API_CODE) {
        setPasscodeCreated(!!passcode);
        setStep("success");
        setSettingsContext({
          ...settingsContext,
          newUserAdded: true,
          updateList: true,
        });
      } else {
        notify(
          getErrorTitle(undefined),
          response.message ?? CONNECTION_ERROR_MESSAGE,
          "error"
        );
      }
    } catch (error: any) {
      // Phone and email errors arrive together here, keyed by field (400 if
      // either is badly formatted, 409 if a duplicate) - e.g. "Enter a valid
      // phone number", "Email already exists", or a cross-field mismatch
      // between the two. Name, role and outlet errors are unaffected and
      // still fall through to the generic toast below.
      const fields = error?.body?.data?.fields;
      if (fields?.phoneNumber || fields?.email) {
        setFieldErrors({
          phoneNumber: fields.phoneNumber,
          email: fields.email,
        });
        setStep("details");
        return;
      }
      const apiErrorMessage = error?.body?.message || CONNECTION_ERROR_MESSAGE;
      notify(getErrorTitle(error?.status), apiErrorMessage, "error");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <SelectRoleModal
        showModal={showModal && step === "role"}
        selectedRole={hasChosenRole ? role : null}
        businessType={businessType}
        onSelectRole={(selected) => {
          setRole(selected);
          setHasChosenRole(true);
        }}
        onViewBreakdown={(r) => {
          setRole(r);
          setHasChosenRole(true);
          setStep("breakdown");
        }}
        onClose={closeFlow}
        onContinue={() => setStep("details")}
      />

      <RolePermissionModal
        showModal={showModal && step === "breakdown"}
        role={role}
        businessType={businessType}
        onBack={() => setStep("role")}
        onClose={closeFlow}
      />

      <TeamMemberDetailsModal
        showModal={showModal && step === "details"}
        role={role}
        outlets={outlets}
        serverFieldErrors={fieldErrors}
        onRoleChange={setRole}
        onViewBreakdown={() => setStep("breakdown")}
        onBack={() => setStep("role")}
        onClose={closeFlow}
        onContinue={(memberDetails) => {
          setDetails(memberDetails);
          if (memberDetails.createDefaultPassword) {
            setStep("passcode");
          } else {
            submitTeamMember(memberDetails);
          }
        }}
      />

      <CreatePasscodeModal
        showModal={showModal && step === "passcode"}
        isSaving={isSaving}
        onBack={() => setStep("details")}
        onClose={closeFlow}
        onSubmit={(passcode) => {
          if (details) submitTeamMember(details, passcode);
        }}
      />

      <TeamMemberSuccessModal
        showModal={showModal && step === "success"}
        passwordCreated={passcodeCreated}
        onDone={closeFlow}
      />
    </>
  );
});

export default AddTeamMemberFlow;
